// src/components/alerts/AlertReadingChart.js
// --- UPDATE (Alert System AI) — NEW: the sensor's readings around an alert, with the rule's
// threshold(s), the learned normal range for this hour (sensor baselines, ±2σ) and the moment
// the alert was raised. Hover for exact values. Plain SVG.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, ButtonGroup, HStack, Skeleton, Text, useColorModeValue, useToken } from '@chakra-ui/react';
import { fetchAlertReadings } from '../../services/activeSensorAlertService';

const HOURS = [6, 24, 72];

function AlertReadingChart({ alertId }) {
    const [hours, setHours] = useState(24);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hover, setHover] = useState(null);
    const svgRef = useRef(null);
    const [cLine, cThreshold, cBand, cGrid, cText, cTrigger] = useToken('colors', ['blue.400', 'red.400', 'purple.300', 'gray.200', 'gray.500', 'orange.400']);
    const gridColor = useColorModeValue(cGrid, 'rgba(255,255,255,0.08)');
    const tipBg = useColorModeValue('white', 'gray.700');

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError(null);
        fetchAlertReadings(alertId, hours)
            .then(d => { if (alive) setData(d); })
            .catch(e => { if (alive) setError(e?.message || 'Readings unavailable.'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [alertId, hours]);

    const geom = useMemo(() => {
        const pts = data?.points || [];
        if (pts.length < 2) return null;
        const W = 640, H = 200, padL = 40, padR = 8, padT = 10, padB = 22;
        const ts = pts.map(p => new Date(p.t).getTime());
        const t0 = ts[0], t1 = ts[ts.length - 1];
        const vals = pts.map(p => p.v);
        const extra = [data.rule?.threshold_value, data.rule?.threshold_value_upper, data.baseline?.lower, data.baseline?.upper].filter(v => v !== null && v !== undefined && Number.isFinite(v));
        let lo = Math.min(...vals, ...extra), hi = Math.max(...vals, ...extra);
        if (hi === lo) { hi += 1; lo -= 1; }
        const pad = (hi - lo) * 0.08;
        lo -= pad; hi += pad;
        const x = t => padL + ((t - t0) / Math.max(1, t1 - t0)) * (W - padL - padR);
        const y = v => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);
        const path = pts.map((p, i) => `${i ? 'L' : 'M'}${x(ts[i]).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
        return { W, H, padL, padR, padT, padB, x, y, lo, hi, path, ts, t0, t1 };
    }, [data]);

    const onMove = (e) => {
        if (!geom || !svgRef.current) return;
        const r = svgRef.current.getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width) * geom.W;
        const t = geom.t0 + ((px - geom.padL) / (geom.W - geom.padL - geom.padR)) * (geom.t1 - geom.t0);
        let best = 0;
        for (let i = 1; i < geom.ts.length; i++) if (Math.abs(geom.ts[i] - t) < Math.abs(geom.ts[best] - t)) best = i;
        setHover(best);
    };

    const pts = data?.points || [];
    const unit = data?.metric === 'psi' ? 'PSI' : data?.metric === 'level' ? 'level' : '';
    const last = pts.length ? pts[pts.length - 1].v : null;

    return (
        <Box>
            <HStack justify="space-between" mb={2} flexWrap="wrap">
                <Text fontSize="sm" fontWeight="700">Sensor readings{unit ? ` · ${unit}` : ''}</Text>
                <ButtonGroup size="xs" isAttached variant="outline">
                    {HOURS.map(h => <Button key={h} onClick={() => setHours(h)} isActive={hours === h}>{h} h</Button>)}
                </ButtonGroup>
            </HStack>
            {loading ? <Skeleton h="200px" borderRadius="lg" /> : error ? (
                <Text fontSize="sm" color="orange.400">{error}</Text>
            ) : !geom ? (
                <Text fontSize="sm" color="gray.500">No readings from this sensor in the last {hours} h.</Text>
            ) : (
                <Box position="relative">
                    <svg ref={svgRef} viewBox={`0 0 ${geom.W} ${geom.H}`} width="100%" role="img" aria-label="Sensor readings chart"
                        onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
                        {[geom.lo, (geom.lo + geom.hi) / 2, geom.hi].map((v, i) => (
                            <g key={i}>
                                <line x1={geom.padL} x2={geom.W - geom.padR} y1={geom.y(v)} y2={geom.y(v)} stroke={gridColor} />
                                <text x={geom.padL - 4} y={geom.y(v) + 4} fontSize="10" textAnchor="end" fill={cText}>{Math.round(v * 10) / 10}</text>
                            </g>
                        ))}
                        {data.baseline && (
                            <rect x={geom.padL} width={geom.W - geom.padL - geom.padR}
                                y={geom.y(data.baseline.upper)} height={Math.max(1, geom.y(data.baseline.lower) - geom.y(data.baseline.upper))}
                                fill={cBand} opacity="0.18" />
                        )}
                        {[data.rule?.threshold_value, data.rule?.threshold_value_upper].filter(v => v !== null && v !== undefined).map((v, i) => (
                            <g key={i}>
                                <line x1={geom.padL} x2={geom.W - geom.padR} y1={geom.y(v)} y2={geom.y(v)} stroke={cThreshold} strokeDasharray="6 4" strokeWidth="1.5" />
                                <text x={geom.W - geom.padR - 2} y={geom.y(v) - 4} fontSize="10" textAnchor="end" fill={cThreshold}>threshold {v}</text>
                            </g>
                        ))}
                        {data.triggered_at && (() => {
                            const tt = new Date(data.triggered_at).getTime();
                            if (tt < geom.t0 || tt > geom.t1) return null;
                            return (
                                <g>
                                    <line x1={geom.x(tt)} x2={geom.x(tt)} y1={geom.padT} y2={geom.H - geom.padB} stroke={cTrigger} strokeWidth="1.5" />
                                    <text x={geom.x(tt) + 3} y={geom.padT + 10} fontSize="10" fill={cTrigger}>alert</text>
                                </g>
                            );
                        })()}
                        <path d={geom.path} fill="none" stroke={cLine} strokeWidth="2" strokeLinejoin="round" />
                        {hover !== null && (
                            <g>
                                <line x1={geom.x(geom.ts[hover])} x2={geom.x(geom.ts[hover])} y1={geom.padT} y2={geom.H - geom.padB} stroke={cText} strokeOpacity="0.4" />
                                <circle cx={geom.x(geom.ts[hover])} cy={geom.y(pts[hover].v)} r="4" fill={cLine} />
                            </g>
                        )}
                        <text x={geom.padL} y={geom.H - 6} fontSize="10" fill={cText}>{new Date(geom.t0).toLocaleString()}</text>
                        <text x={geom.W - geom.padR} y={geom.H - 6} fontSize="10" textAnchor="end" fill={cText}>{new Date(geom.t1).toLocaleString()}</text>
                    </svg>
                    {hover !== null && (
                        <Box position="absolute" top={1} right={2} bg={tipBg} boxShadow="md" borderRadius="md" px={2} py={1} fontSize="xs" pointerEvents="none">
                            <Text fontWeight="700">{pts[hover].v}{unit ? ` ${unit}` : ''}</Text>
                            <Text color="gray.500">{new Date(pts[hover].t).toLocaleString()}</Text>
                        </Box>
                    )}
                </Box>
            )}
            {data && !loading && !error && (
                <HStack spacing={4} mt={1} fontSize="xs" color="gray.500" flexWrap="wrap">
                    {last !== null && <Text>Latest: <b>{last}</b></Text>}
                    {data.rule && <Text color="red.400">— — threshold ({String(data.rule.condition_type || '').replace(/_/g, ' ')})</Text>}
                    {data.baseline && <Text color="purple.400">▮ {data.baseline.label}: {Math.round(data.baseline.lower * 10) / 10}–{Math.round(data.baseline.upper * 10) / 10}</Text>}
                    <Text>{pts.length} points</Text>
                </HStack>
            )}
        </Box>
    );
}

AlertReadingChart.propTypes = { alertId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired };

export default AlertReadingChart;
// --- END UPDATE
